import React, { useState } from "react";
import { useFormik } from "formik";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../../Components/Others/ToastServices";
import { TextMediumSM } from "../../../../Components/Text/MediumText";
import {
    CancelButton,
    SubmitButton,
} from "../../../../Components/Utils/Buttons";
import FormikSelectTwo from "../../../../Components/Utils/Select";
import EditIcon from "../../../../Components/Icons/Edit";
import DeleteIcon from "../../../../Components/Icons/Delete";
import { NuInput } from "../../../../Components/Utils/Input";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import axiosInstance from "../../../../Services/axiosCommon";
import * as yup from "yup";

function POLimit() {
    const toast = useToast();
    const queryClient = useQueryClient();

    const [showEditDialog, setShowEditDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    // React Query: Fetch dropdown list of users for PO approval cost
    const { data: userDropdown = [] } = useQuery({
        queryKey: ["user-po-approval-cost-dropdown"],
        queryFn: async () => {
            try {
                const res = await axiosInstance.get("/user/po-approval-cost/drop-down");
                const rawData = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                if (rawData.length > 0) {
                    return rawData.map((u) => ({
                        label: u.name,
                        value: String(u.id),
                    }));
                }
            } catch (err) {
                console.warn("Failed fetching /user/po-approval-cost/drop-down, trying fallback", err);
            }
            // Fallback if specific endpoint fails or returns empty
            const res = await axiosInstance.get("/user/approval-cost/drop-down");
            const rawData = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            return rawData.map((u) => ({
                label: u.name,
                value: String(u.id),
            }));
        },
        staleTime: 60000,
    });

    // React Query: Fetch PO approval cost limits list
    const { data = [], isLoading: loading } = useQuery({
        queryKey: ["user-po-approval-cost-list"],
        queryFn: async () => {
            const res = await axiosInstance.get("/user/po-approval-cost");
            if (res.data && res.data.status !== false) {
                const rawList = res.data.data || [];
                return rawList.filter(
                    (item) =>
                        (item.po_approvalCost !== null && item.po_approvalCost !== undefined) ||
                        (item.approvalCost !== null && item.approvalCost !== undefined)
                );
            }
            return [];
        },
        staleTime: 5000,
    });

    // React Query: Add Mutation
    const addMutation = useMutation({
        mutationFn: async (payload) => {
            const res = await axiosInstance.post("/user/po-approval-cost", payload);
            return res.data;
        },
        onSuccess: (responseData) => {
            if (responseData?.status !== false) {
                toast.open({
                    type: "success",
                    message: "Success",
                    description: responseData?.msg || "PO approval cost limit saved successfully.",
                });
                queryClient.invalidateQueries({ queryKey: ["user-po-approval-cost-list"] });
            } else {
                toast.open({
                    type: "error",
                    message: "Error",
                    description: responseData?.msg || responseData?.error || "Failed to save PO limit.",
                });
            }
        },
        onError: (err) => {
            toast.open({
                type: "error",
                message: "Error",
                description: err?.response?.data?.error || err?.response?.data?.msg || "Network error.",
            });
        },
    });

    // React Query: Update / Toggle Status Mutation
    const updateMutation = useMutation({
        mutationFn: async ({ userId, payload }) => {
            const res = await axiosInstance.put(`/user/po-approval-cost/${userId}`, payload);
            return res.data;
        },
        onSuccess: (responseData) => {
            if (responseData?.status !== false) {
                toast.open({
                    type: "success",
                    message: "Success",
                    description: responseData?.msg || "PO approval cost limit updated successfully.",
                });
                setShowEditDialog(false);
                setEditingItem(null);
                queryClient.invalidateQueries({ queryKey: ["user-po-approval-cost-list"] });
            } else {
                toast.open({
                    type: "error",
                    message: "Error",
                    description: responseData?.msg || responseData?.error || "Failed to update PO limit.",
                });
            }
        },
        onError: (err) => {
            toast.open({
                type: "error",
                message: "Error",
                description: err?.response?.data?.error || err?.response?.data?.msg || "Network error.",
            });
        },
    });

    // React Query: Delete Mutation
    const deleteMutation = useMutation({
        mutationFn: async (userId) => {
            const res = await axiosInstance.delete(`/user/po-approval-cost/${userId}`);
            return res.data;
        },
        onSuccess: (responseData) => {
            if (responseData?.status !== false) {
                toast.open({
                    type: "success",
                    message: "Deleted",
                    description: responseData?.msg || "PO approval limit deleted successfully.",
                });
                setIsDeleteModalOpen(false);
                setDeleteItem(null);
                queryClient.invalidateQueries({ queryKey: ["user-po-approval-cost-list"] });
            } else {
                toast.open({
                    type: "error",
                    message: "Error",
                    description: responseData?.msg || responseData?.error || "Failed to delete PO approval limit.",
                });
            }
        },
        onError: (err) => {
            toast.open({
                type: "error",
                message: "Error",
                description: err?.response?.data?.error || err?.response?.data?.msg || "Network error.",
            });
        },
    });

    // Form for Adding New Approval Limit
    const formikAdd = useFormik({
        initialValues: {
            userId: "",
            po_approvalCost: "",
            is_po_approvalCost: "true",
        },
        validationSchema: yup.object({
            userId: yup.string().required("User is required!"),
            po_approvalCost: yup
                .number()
                .required("Approval cost is required!")
                .positive("Must be positive!"),
            is_po_approvalCost: yup.string().required("Status is required!"),
        }),
        onSubmit: (values, { resetForm }) => {
            const payload = {
                userId: Number(values.userId),
                po_approvalCost: parseFloat(values.po_approvalCost),
                is_po_approvalCost: values.is_po_approvalCost === "true" || values.is_po_approvalCost === true,
            };

            addMutation.mutate(payload, {
                onSuccess: (resData) => {
                    if (resData?.status !== false) {
                        resetForm();
                    }
                },
            });
        },
    });

    // Form for Editing Approval Limit
    const formikEdit = useFormik({
        enableReinitialize: true,
        initialValues: {
            userId: editingItem ? String(editingItem.userId) : "",
            po_approvalCost: editingItem ? (editingItem.po_approvalCost ?? editingItem.approvalCost ?? "") : "",
            is_po_approvalCost: editingItem
                ? String(editingItem.is_po_approvalCost ?? editingItem.is_approvalCost ?? "true")
                : "true",
        },
        validationSchema: yup.object({
            po_approvalCost: yup
                .number()
                .required("Approval cost is required!")
                .positive("Must be positive!"),
            is_po_approvalCost: yup.string().required("Status is required!"),
        }),
        onSubmit: (values) => {
            const payload = {
                po_approvalCost: parseFloat(values.po_approvalCost),
                is_po_approvalCost: values.is_po_approvalCost === "true" || values.is_po_approvalCost === true,
            };

            updateMutation.mutate({
                userId: editingItem.userId,
                payload,
            });
        },
    });

    // Confirm delete handler
    const confirmDelete = () => {
        if (!deleteItem) return;
        deleteMutation.mutate(deleteItem.userId);
    };

    const getInitials = (name) => {
        if (!name) return "U";
        const names = name.trim().split(" ");
        if (names.length > 1) {
            return (names[0][0] + names[1][0]).toUpperCase();
        }
        return names[0][0].toUpperCase();
    };

    return (
        <>
            <div className="ml-5 mb-2 flex items-center justify-between pr-5">
                <TextMediumSM content={"Purchase Order"} />
                {/* <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full font-semibold border border-emerald-200">
                    PO Limit Settings
                </span> */}
            </div>

            {/* Add Form */}
            <form onSubmit={formikAdd.handleSubmit}>
                <div className="flex flex-wrap items-start justify-start gap-1">
                    <FormikSelectTwo
                        label="User Name"
                        placeholder="Choose User"
                        Options={userDropdown}
                        formik={formikAdd}
                        name="userId"
                        isRequired={true}
                        width="w-full md:w-1/3 lg:w-1/4"
                        zindex={60}
                    />

                    <NuInput
                        label="Approval Cost Limit"
                        placeholder="e.g. 50000"
                        type="number"
                        formik={formikAdd}
                        name="po_approvalCost"
                        isRequired={true}
                        width="w-full md:w-1/3 lg:w-1/4"
                    />

                    <FormikSelectTwo
                        label="Status"
                        placeholder="Choose"
                        Options={[
                            { value: "true", label: "Enabled" },
                            { value: "false", label: "Disabled" },
                        ]}
                        formik={formikAdd}
                        isRequired={true}
                        name="is_po_approvalCost"
                        width="w-full md:w-1/3 lg:w-1/4"
                        zindex={50}
                    />

                    <div className="w-full md:w-24 h-12 lg:mt-8 px-2">
                        <SubmitButton name={addMutation.isPending ? "Adding..." : "Add"} disabled={addMutation.isPending} fullWidth />
                    </div>
                </div>
            </form>

            {/* Table listing approval cost limits */}
            <div className="w-full overflow-x-auto px-5 mt-4">
                {loading ? (
                    <div className="w-full py-8 text-center text-sm font-semibold text-gray-500 animate-pulse">
                        Loading PO approval cost limits...
                    </div>
                ) : (
                    <table className="w-full min-w-[650px] border-collapse">
                        <thead>
                            <tr className="border-b border-gray-300 text-gray-700">
                                <th className="w-[40%] text-left text-sm font-semibold py-2.5 px-4">
                                    User
                                </th>
                                <th className="w-[25%] text-left text-sm font-semibold py-2.5 px-4">
                                    Approval Cost Limit
                                </th>
                                <th className="w-[20%] text-left text-sm font-semibold py-2.5 px-4">
                                    Status
                                </th>
                                <th className="w-[15%] text-center text-sm font-semibold py-2.5 px-4">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {data && data.length > 0 ? (
                                data.map((item, idx) => {
                                    const cost = item.po_approvalCost ?? item.approvalCost ?? 0;
                                    const statusActive = item.is_po_approvalCost ?? item.is_approvalCost ?? true;
                                    return (
                                        <tr
                                            key={item.userId || idx}
                                            className="even:bg-white odd:bg-slate-50 border-b border-gray-100 hover:bg-slate-100/60 transition-colors"
                                        >
                                            {/* User column */}
                                            <td className="w-[40%] px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        style={{
                                                            width: "34px",
                                                            height: "34px",
                                                            borderRadius: "50%",
                                                            backgroundColor: "#048178",
                                                            color: "white",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: "12px",
                                                            fontWeight: "700",
                                                            border: "2px solid #fff",
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                        }}
                                                    >
                                                        {getInitials(item.name)}
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-xs font-bold text-gray-800">
                                                            {item.name || "N/A"}
                                                        </span>
                                                        {item.email && (
                                                            <span className="text-[11px] text-gray-500 font-medium">
                                                                {item.email}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Approval Cost column */}
                                            <td className="w-[25%] text-xs font-bold text-gray-800 px-4 py-3">
                                                <span className="bg-gray-100 px-2.5 py-1 rounded-md border border-gray-200">
                                                    {Number(cost).toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </span>
                                            </td>

                                            {/* Status column */}
                                            <td className="w-[20%] text-xs px-4 py-3">
                                                <div
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer ${statusActive
                                                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                                        : "bg-rose-100 text-rose-800 border border-rose-300"
                                                        }`}
                                                >
                                                    <span
                                                        className={`w-2 h-2 rounded-full ${statusActive ? "bg-emerald-600" : "bg-rose-600"
                                                            }`}
                                                    />
                                                    {statusActive ? "Enabled" : "Disabled"}
                                                </div>
                                            </td>

                                            {/* Action column */}
                                            <td className="w-[15%] text-center py-3">
                                                <div className="flex gap-3 justify-center items-center">
                                                    <button
                                                        type="button"
                                                        className="cursor-pointer hover:scale-110 transition-transform"
                                                        onClick={() => {
                                                            setEditingItem(item);
                                                            setShowEditDialog(true);
                                                        }}
                                                        title="Edit Limit"
                                                    >
                                                        <EditIcon color="green" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="cursor-pointer hover:scale-110 transition-transform"
                                                        onClick={() => {
                                                            setDeleteItem(item);
                                                            setIsDeleteModalOpen(true);
                                                        }}
                                                        title="Delete Limit"
                                                    >
                                                        <DeleteIcon />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={4} className="text-center py-6 text-gray-400 text-xs">
                                        No user PO approval cost limits found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Edit Modal */}
            {showEditDialog && (
                <div className="fixed top-0 left-0 w-full h-full bg-gray-800/50 backdrop-blur-xs z-[9999] flex justify-center items-center p-4">
                    <div className="animate-scale-in w-full max-w-lg bg-white p-6 rounded-xl shadow-xl overflow-y-auto">
                        <h2 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100">
                            Edit User PO Approval Cost Limit
                        </h2>
                        <form onSubmit={formikEdit.handleSubmit}>
                            <div className="flex flex-col gap-3 mb-4">
                                <FormikSelectTwo
                                    label="User Name"
                                    placeholder="Choose User"
                                    Options={userDropdown}
                                    formik={formikEdit}
                                    name="userId"
                                    isRequired={true}
                                    width="w-full"
                                    px="0"
                                    disabled={true}
                                />

                                <NuInput
                                    label="Approval Cost (SAR)"
                                    placeholder="e.g. 50000"
                                    type="number"
                                    formik={formikEdit}
                                    name="po_approvalCost"
                                    isRequired={true}
                                    width="w-full"
                                    px="0"
                                />

                                <FormikSelectTwo
                                    label="Status"
                                    placeholder="Choose"
                                    Options={[
                                        { value: "true", label: "Enabled" },
                                        { value: "false", label: "Disabled" },
                                    ]}
                                    formik={formikEdit}
                                    isRequired={true}
                                    name="is_po_approvalCost"
                                    width="w-full"
                                    px="0"
                                />
                            </div>

                            <div className="flex justify-end gap-2.5 pt-3 border-t border-gray-100">
                                <CancelButton handleClick={() => setShowEditDialog(false)} />
                                <SubmitButton
                                    name={updateMutation.isPending ? "Updating..." : "Update"}
                                    disabled={updateMutation.isPending}
                                />
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <ModalContainer
                    visiable={isDeleteModalOpen}
                    title="Warning"
                    content={`Are you sure you want to remove PO approval cost limit for ${deleteItem?.name}?`}
                    handleClick={confirmDelete}
                    closeModal={() => {
                        setIsDeleteModalOpen(false);
                        setDeleteItem(null);
                    }}
                />
            )}
        </>
    );
}

export default POLimit;
